import {Box, Button, Grid, Stack, TextField, useTheme} from "@mui/material";
import {observer} from 'mobx-react';
import {useNavigate, useParams} from "react-router-dom";
import * as React from "react";
import {useApplication} from "../../ApplicationProvider";
import {useNavigatorFeature} from "../../useFeature";
import LayoutContentWrapper from "../../components/layout/LayoutContentWrapper";
import FormSection from "../../forms/FormSection";
import AuthorizationFeature from "./AuthorizationFeature";
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FeedbackIcon from '@mui/icons-material/Feedback';
import FreeOrderComponent from "./action_requests/FreeOrderConfirmationRequestComponent";
import AuthorizationCustomerOnboardingScreen from "./action_requests/AuthorizationCustomerOnboardingScreen";
import Typography from "@mui/material/Typography";
import LayoutContentToolbar from "../../components/layout/LayoutContentToolbar";
import {AUTHORIZATIONS_PATH} from "../../routes";
import AdvancePaymentRequestComponent from "./action_requests/AdvancePaymentRequestComponent";
import AuthorizationDeliverySiteOnboardingScreen from "./action_requests/AuthorizationDeliverySiteOnboardingScreen";
import AllowanceConfirmationRequestComponent from "./action_requests/AllowanceConfirmationRequestComponent";
import JudicialManagementRequestComponent from "./action_requests/JudicialManagementRequestComponent";

function AuthorizationScreen() {
    const application = useApplication();
    const theme = useTheme();
    const navigator = useNavigate();
    const {id} = useParams();
    const feature = useNavigatorFeature(() => new AuthorizationFeature(application, navigator, id), navigator, [id]);


    const isFeatureReady = (feature) => {
        return feature && feature.loaded;
    };

    const handleCommentsChange = (e) => {
        feature.setObservation(e.target.value);
    };

    const componentRegistry = {
        'Pedido Sin Cargo': {
            component: FreeOrderComponent,
            getProps: feature => ({order: feature.order()})
        },
        'Pedido Con Pago Anticipado': {
            component: AdvancePaymentRequestComponent,
            getProps: feature => ({
                order: feature.order(),
                paymentProof: feature.advancePaymentProof,
                orderPrices: feature.orderPrices})
        },
        'Alta de Cliente - Logística': {
            component: AuthorizationCustomerOnboardingScreen,
            getProps: feature => ({taxpayerIdentificationNumber: feature.taxpayerIdentificationNumber()})
        },
        'Alta de Cliente - Comercial': {
            component: AuthorizationCustomerOnboardingScreen,
            getProps: feature => ({taxpayerIdentificationNumber: feature.taxpayerIdentificationNumber()})
        },
        'Alta de Cliente - Finanzas': {
            component: AuthorizationCustomerOnboardingScreen,
            getProps: feature => ({taxpayerIdentificationNumber: feature.taxpayerIdentificationNumber()})
        },
        'Alta de Cliente - Impuestos': {
            component: AuthorizationCustomerOnboardingScreen,
            getProps: feature => ({taxpayerIdentificationNumber: feature.taxpayerIdentificationNumber()})
        },
        'Alta de Punto de Entrega - Impuestos': {
            component: AuthorizationDeliverySiteOnboardingScreen,
            getProps: feature => ({deliverySiteOnboardingId: feature.deliverySiteOnboardingId(), showFiscalProfile: true})
        },
        'Alta de Punto de Entrega - Logística': {
            component: AuthorizationDeliverySiteOnboardingScreen,
            getProps: feature => ({deliverySiteOnboardingId: feature.deliverySiteOnboardingId()})
        },
        'Bonificación': {
            component: AllowanceConfirmationRequestComponent,
            getProps: feature => ({allowance: feature.allowance()})
        },
        'Pase a Gestión Judicial': {
            component: JudicialManagementRequestComponent,
            getProps: feature => {
                const judicialManagementRequest = feature.judicialManagementRequest();
                return {
                    customer: judicialManagementRequest.customer,
                    requestedStage: judicialManagementRequest.requestedStage,
                    reason: judicialManagementRequest.reason,
                    overdueInvoicesCount: judicialManagementRequest.overdueInvoicesCount,
                    overdueInvoicesTotalPendingBalance: judicialManagementRequest.overdueInvoicesTotalPendingBalance,
                };
            }
        },
    };

    const renderContent = () => {
        return isFeatureReady(feature) && (
            <div>

                <LayoutContentToolbar
                    title="Autorización"
                    breadcrumbs={[
                        {label: 'Autorizaciones', path: AUTHORIZATIONS_PATH},
                        {label: 'Detalle'},
                    ]}
                />
                <FormSection>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Solicitante"
                                value={feature.requester()}
                                fullWidth
                                InputProps={{readOnly: true}}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Acción"
                                value={feature.action()}
                                fullWidth
                                InputProps={{readOnly: true}}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Fecha"
                                value={feature.requestTimestamp()}
                                fullWidth
                                InputProps={{readOnly: true}}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Estado"
                                value={feature.state()}
                                fullWidth
                                InputProps={{readOnly: true}}
                            />
                        </Grid>
                    </Grid>
                </FormSection>

                <div>
                    {(() => {
                        const actionName = feature.action();
                        const entry = componentRegistry[actionName];
                        if (!entry) {
                            return <div>No hay componente para la acción</div>;
                        }
                        const RequestedActionComponent = entry.component;
                        const requestedActionsProps    = entry.getProps(feature);
                        return <RequestedActionComponent {...requestedActionsProps} />;
                    })()}
                </div>

                <FormSection title="Aclaraciones">
                    <TextField
                        label="Comentarios"
                        value={feature.authorization.observations || ""}
                        onChange={(e) => feature.setObservation(e.target.value)}
                        InputProps={{ disabled: feature.stateIsClosed() }}
                        fullWidth
                        multiline
                        minRows={4}
                        placeholder="Escriba sus aclaraciones aquí..."
                    />
                </FormSection>
                <Stack direction="row" spacing={2} mt={2}>
                    { !feature.stateIsClosed() &&
                        <Button
                            disabled={feature.stateIsClosed()}
                            onClick={() => feature.approveAuthorization()}
                            color="success"
                            variant="contained"
                            startIcon={<CheckBoxIcon />}
                        >
                            Aceptar
                        </Button>
                    }
                    { !feature.stateIsClosed() &&
                        <Button
                            disabled={feature.denyIsDisabled()}
                            onClick={() => feature.denyAuthorization()}
                            color="error"
                            variant="contained"
                            startIcon={<DisabledByDefaultIcon />}
                        >
                            Denegar
                        </Button>
                    }
                    { !feature.stateIsClosed() && !feature.isAdvancePaymentRequest() && !feature.isAllowanceConfirmationRequest() && !feature.isJudicialManagementRequest() &&
                        <Button
                            disabled={feature.stateIsClosed()}
                            onClick={() => feature.requiresChanges()}
                            color="primary"
                            variant="contained"
                            startIcon={<FeedbackIcon/>}
                        >
                            Pedir Cambios
                        </Button>
                    }
                </Stack>
            </div>
        );
    };

    return (
        <LayoutContentWrapper isLoading={!isFeatureReady(feature)} renderContent={renderContent} />
    );
}

export default observer(AuthorizationScreen);
